/**
 * Profile images are loaded by the browser via direct HTTPS GET to the public object URL
 * (no proxy through auth-admin). Shape: `${base}/${object_key}` with optional `_small` / `_medium` / `_mini` variant.
 *
 * Base URL follows the Admin app API environment (same notion as backend `ENV_TYPE` test vs prod buckets).
 * Upload pipeline stores the original object key in API `object_key`; S3 also has `_medium`, `_small`, `_mini` siblings (same directory).
 */

import { API_ENV_PROD, API_ENV_TEST, normalizeApiEnvironment } from './apiEnvironment';

/** @typedef {'original' | 'medium' | 'small' | 'mini'} ProfileImageVariant */

const PROFILE_PHOTO_S3_BASE_BY_ENV = Object.freeze({
  [API_ENV_TEST]: 'https://test-fancian-user-profile-photo.s3.us-east-1.amazonaws.com',
  [API_ENV_PROD]: 'https://fancian-user-profile-photo.s3.us-east-1.amazonaws.com',
});

/**
 * Public virtual-hosted-style S3 base URL for profile objects (matches test/prod buckets).
 * @param {string} [apiEnvironment] `'test'` | `'prod'` (see `apiEnvironment.js`)
 */
export function getProfileImageBucketBaseUrl(apiEnvironment) {
  const env = normalizeApiEnvironment(apiEnvironment);
  return PROFILE_PHOTO_S3_BASE_BY_ENV[env] ?? PROFILE_PHOTO_S3_BASE_BY_ENV[API_ENV_PROD];
}

/**
 * Encode each path segment for use in a URL (handles +, spaces, Unicode; matches common S3 URL expectations).
 * @param {string} key
 */
export function encodeS3ObjectKeyForUrl(key) {
  if (typeof key !== 'string' || key.trim() === '') return '';
  return key
    .split('/')
    .map((seg) => encodeURIComponent(seg))
    .join('/');
}

/**
 * From a stored original object key, derive the variant key (user-api `DeriveProfilePhotoVariantObjectKeys` / resize naming).
 * Original file is `<base>.<ext>`; variants are `<base>_medium.<ext>`, `<base>_small.<ext>`, `<base>_mini.<ext>`.
 * If the basename already ends with `_original`, it is stripped before applying the variant (legacy rows).
 *
 * @param {string} objectKey
 * @param {ProfileImageVariant} variant
 */
export function objectKeyForVariant(objectKey, variant) {
  if (!objectKey || variant === 'original') return objectKey;
  const slash = objectKey.lastIndexOf('/');
  const dir = slash >= 0 ? objectKey.slice(0, slash + 1) : '';
  const file = slash >= 0 ? objectKey.slice(slash + 1) : objectKey;
  const dot = file.lastIndexOf('.');
  if (dot <= 0) return objectKey;
  let stem = file.slice(0, dot);
  const ext = file.slice(dot + 1);
  stem = stem.replace(/_original$/i, '');
  stem = stem.replace(/_medium$/i, '');
  stem = stem.replace(/_small$/i, '');
  stem = stem.replace(/_mini$/i, '');
  return `${dir}${stem}_${variant}.${ext}`;
}

/**
 * Builds public profile image URL from `object_key` and Admin API environment (test/prod bucket).
 * When `opts.apiEnvironment` is set, **`s3_endpoint` from the API is ignored** for the host (object still comes from that bucket pair).
 * If `apiEnvironment` is omitted, falls back to `user.s3_endpoint` / CDN from the API.
 * Defaults to **`small`** (96×96) for admin thumbnails.
 *
 * @param {object|null|undefined} user
 * @param {{ variant?: ProfileImageVariant, apiEnvironment?: string }} [opts]
 * @returns {string|null}
 */
export function getProfileImageUrl(user, opts = {}) {
  const variant = opts.variant ?? 'small';
  if (!user || typeof user !== 'object') return null;
  let key = user.object_key ?? user.ObjectKey;
  if (typeof key !== 'string' || key.trim() === '') return null;

  let base;
  if (opts.apiEnvironment !== undefined && opts.apiEnvironment !== null) {
    base = getProfileImageBucketBaseUrl(opts.apiEnvironment);
  } else {
    base = user.s3_endpoint ?? user.s3Endpoint ?? user.S3Endpoint;
  }
  if (typeof base !== 'string' || base.trim() === '') return null;
  if (variant !== 'original') {
    key = objectKeyForVariant(key, variant);
  }
  const b = base.replace(/\/+$/, '');
  const k = encodeS3ObjectKeyForUrl(key.replace(/^\/+/, ''));
  return `${b}/${k}`;
}
